/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18nInstance from "@/i18n";
import {
  ChevronLeft, ChevronRight, Plus, X, Trash2, Loader2, CalendarDays, List, CalendarRange,
  Bell, Briefcase, User as UserIcon,
} from "lucide-react";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const START_HOUR = 7;
const END_HOUR = 21;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const ROW_H = 52;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const off = (d.getDay() + 6) % 7; return addDays(d, -off); };
const monthLong = (d) => d.toLocaleDateString(i18nInstance.language, { month: "long", year: "numeric" });
const monthShort = (d) => d.toLocaleDateString(i18nInstance.language, { month: "short" });
const dowShort = () => { const mon = mondayOf(new Date()); return Array.from({ length: 7 }, (_, i) => addDays(mon, i).toLocaleDateString(i18nInstance.language, { weekday: "short" })); };
const sameDay = (iso, dayStr) => {
  try { return ymd(new Date(iso)) === dayStr; } catch { return false; }
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(i18nInstance.language, { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function Calendar() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month");      // month | week | agenda
  const [ctx, setCtx] = useState("all");          // all | firma | personal
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [modal, setModal] = useState(null);
  const dow = useMemo(() => dowShort(), []);

  const load = async () => {
    const from = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString();
    const to = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0, 23, 59, 59).toISOString();
    try {
      const r = await api.get(`/calendar?from=${from}&to=${to}&context=${ctx}`);
      setEvents(r.data.events);
      setJobs(r.data.jobs);
    } catch { toast.error(t("calendar.loadError")); }
  };
  useEffect(() => { load(); }, [cursor, ctx]);

  // unified items
  const items = useMemo(() => {
    const evs = events.map((e) => ({ kind: "event", ...e }));
    const jbs = jobs.map((j) => ({ kind: "job", id: j.id, title: j.title, note: j.client_name || "", context: "firma", start: j.scheduled_start, end: j.scheduled_end, status: j.status }));
    return [...evs, ...jbs];
  }, [events, jobs]);

  const itemsForDay = (dayStr) => items.filter((it) => sameDay(it.start, dayStr) || (it.end && it.start <= dayStr + "T23:59:59" && it.end >= dayStr));

  // month grid (Mon-first, 6 weeks)
  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const start = new Date(first); start.setDate(1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const todayStr = ymd(new Date());

  const weekDays = useMemo(() => {
    const mon = mondayOf(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
  }, [cursor]);

  const stepPrev = () => setCursor(view === "week" ? addDays(cursor, -7) : new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const stepNext = () => setCursor(view === "week" ? addDays(cursor, 7) : new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  const headerLabel = view === "week"
    ? `${weekDays[0].getDate()} ${monthShort(weekDays[0])} – ${weekDays[6].getDate()} ${monthShort(weekDays[6])}`
    : monthLong(cursor);

  const openSlot = (d, h) => setModal({
    context: ctx === "personal" ? "personal" : "firma", date: ymd(d),
    time: `${String(h).padStart(2, "0")}:00`, endTime: `${String(Math.min(h + 1, 23)).padStart(2, "0")}:00`,
    title: "", note: "", reminder: "60", reminder_email: true,
  });


  const agenda = useMemo(() => {
    const upcoming = items
      .filter((it) => it.start && new Date(it.start) >= new Date(todayStr + "T00:00:00"))
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    const groups = {};
    upcoming.forEach((it) => { const k = ymd(new Date(it.start)); (groups[k] = groups[k] || []).push(it); });
    return Object.entries(groups);
  }, [items]);

  const dotColor = (it) => it.kind === "job" ? "#22C55E" : it.context === "personal" ? "#A78BFA" : "#00BFFF";

  return (
    <div className="p-5 lg:p-10 max-w-6xl mx-auto" data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#00BFFF]">{t("calendar.overline")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("calendar.title")}</h1>
        </div>
        <button onClick={() => setModal({ context: ctx === "personal" ? "personal" : "firma", date: todayStr, time: "09:00", endTime: "", title: "", note: "", reminder: "60", reminder_email: true })} className="btn-primary flex items-center gap-2" data-testid="new-event-btn">
          <Plus size={16} /> {t("calendar.newEvent")}
        </button>
      </div>

      {/* Controls */}
      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={stepPrev} className="btn-ink p-2" data-testid="prev-month"><ChevronLeft size={18} /></button>
          <div className="font-display font-bold text-lg min-w-44 text-center px-2" data-testid="cal-title">{headerLabel}</div>
          <button onClick={stepNext} className="btn-ink p-2" data-testid="next-month"><ChevronRight size={18} /></button>
          <button onClick={() => setCursor(new Date())} className="btn-outline text-sm" data-testid="today-btn">{t("calendar.today")}</button>
        </div>
        <div className="flex items-center gap-2">
          {/* context filter */}
          <div className="flex rounded-lg border border-[#1E293B] overflow-hidden">
            {[{ k: "all", l: t("calendar.ctxAll") }, { k: "firma", l: t("calendar.ctxFirma") }, { k: "personal", l: t("calendar.ctxPersonal") }].map((c) => (
              <button key={c.k} onClick={() => setCtx(c.k)} data-testid={`ctx-${c.k}`} className={`px-3 py-2 text-xs font-semibold ${ctx === c.k ? "bg-[#00BFFF] text-[#03070E]" : "text-[#94A3B8] hover:text-[#E2E8F0]"}`}>{c.l}</button>
            ))}
          </div>
          {/* view toggle */}
          <div className="flex rounded-lg border border-[#1E293B] overflow-hidden">
            <button onClick={() => setView("month")} data-testid="view-month" className={`px-3 py-2 ${view === "month" ? "bg-[#111C30] text-[#00BFFF]" : "text-[#94A3B8]"}`}><CalendarDays size={16} /></button>
            <button onClick={() => setView("week")} data-testid="view-week" className={`px-3 py-2 ${view === "week" ? "bg-[#111C30] text-[#00BFFF]" : "text-[#94A3B8]"}`}><CalendarRange size={16} /></button>
            <button onClick={() => setView("agenda")} data-testid="view-agenda" className={`px-3 py-2 ${view === "agenda" ? "bg-[#111C30] text-[#00BFFF]" : "text-[#94A3B8]"}`}><List size={16} /></button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex gap-4 text-xs text-[#94A3B8]">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#00BFFF]" /> {t("calendar.ctxFirma")}</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#A78BFA]" /> {t("calendar.ctxPersonal")}</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" /> {t("calendar.legendJob")}</span>
      </div>

      {view === "month" ? (
        <div className="mt-4 card-flat overflow-hidden" data-testid="month-grid">
          <div className="grid grid-cols-7 border-b border-[#1E293B]">
            {dow.map((d) => <div key={d} className="p-2 text-center text-xs font-bold text-[#94A3B8] uppercase">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {grid.map((d, i) => {
              const ds = ymd(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const dayItems = itemsForDay(ds);
              return (
                <div key={i} onClick={() => setModal({ context: ctx === "personal" ? "personal" : "firma", date: ds, time: "09:00", endTime: "", title: "", note: "", reminder: "60", reminder_email: true })}
                  className={`min-h-[88px] p-1.5 border-b border-r border-[#1E293B] cursor-pointer hover:bg-[#111C30]/40 ${inMonth ? "" : "opacity-40"}`} data-testid={`day-${ds}`}>
                  <div className={`text-xs font-mono-plex mb-1 w-6 h-6 flex items-center justify-center rounded-full ${ds === todayStr ? "bg-[#00BFFF] text-[#03070E] font-bold" : "text-[#94A3B8]"}`}>{d.getDate()}</div>
                  <div className="space-y-1">
                    {dayItems.slice(0, 3).map((it) => (
                      <div key={it.kind + it.id} onClick={(e) => { e.stopPropagation(); openItem(it); }}
                        className="text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1" style={{ background: dotColor(it) + "22", color: dotColor(it) }} data-testid={`item-${it.id}`}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dotColor(it) }} />
                        {it.title}
                      </div>
                    ))}
                    {dayItems.length > 3 && <div className="text-[10px] text-[#94A3B8] pl-1">{t("calendar.moreItems", { count: dayItems.length - 3 })}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : view === "week" ? (
        <WeekGrid days={weekDays} items={items} todayStr={todayStr} onSlot={openSlot} onItem={openItem} dotColor={dotColor} dow={dow} />
      ) : (
        <div className="mt-4 space-y-5" data-testid="agenda-view">
          {agenda.length === 0 ? (
            <div className="card-flat p-10 text-center text-sm text-[#94A3B8]">{t("calendar.noEvents")}</div>
          ) : agenda.map(([day, list]) => (
            <div key={day}>
              <div className="overline mb-2">{new Date(day + "T00:00:00").toLocaleDateString(i18nInstance.language, { weekday: "long", day: "numeric", month: "long" })}</div>
              <div className="space-y-2">
                {list.map((it) => (
                  <button key={it.kind + it.id} onClick={() => openItem(it)} className="w-full text-left card-flat p-4 flex items-center gap-3" data-testid={`agenda-item-${it.id}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dotColor(it) }} />
                    {it.kind === "job" ? <Briefcase size={16} className="text-[#22C55E]" /> : it.context === "personal" ? <UserIcon size={16} className="text-[#A78BFA]" /> : <Briefcase size={16} className="text-[#00BFFF]" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{it.title}</div>
                      {it.note && <div className="text-xs text-[#94A3B8] truncate">{it.note}</div>}
                    </div>
                    <div className="text-xs font-mono-plex text-[#94A3B8] shrink-0">{it.kind === "job" ? t("calendar.job") : fmtTime(it.start)}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <EventModal data={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} nav={nav} />}
    </div>
  );

  function openItem(it) {
    if (it.kind === "job") { nav(`/app/lucrari/${it.id}`); return; }
    const d = new Date(it.start);
    setModal({
      id: it.id, context: it.context, date: ymd(d),
      time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      endTime: it.end ? (() => { const e = new Date(it.end); return `${String(e.getHours()).padStart(2, "0")}:${String(e.getMinutes()).padStart(2, "0")}`; })() : "",
      title: it.title, note: it.note || "",
      reminder: it.reminder_minutes == null ? "" : String(it.reminder_minutes),
      reminder_email: it.reminder_email !== false,
    });
  }
}

function EventModal({ data, onClose, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState(data);
  const [busy, setBusy] = useState(false);
  const isEdit = !!data.id;
  const REMINDERS = [
    { v: "", l: t("calendar.remNone") },
    { v: "0", l: t("calendar.remAtTime") },
    { v: "10", l: t("calendar.rem10") },
    { v: "30", l: t("calendar.rem30") },
    { v: "60", l: t("calendar.rem60") },
    { v: "1440", l: t("calendar.rem1440") },
  ];

  const buildStart = () => new Date(`${f.date}T${f.time || "09:00"}:00`).toISOString();
  const buildEnd = () => f.endTime ? new Date(`${f.date}T${f.endTime}:00`).toISOString() : null;

  const save = async () => {
    if (!f.title.trim()) return toast.error(t("calendar.addTitleError"));
    setBusy(true);
    const payload = {
      title: f.title, note: f.note, context: f.context,
      start: buildStart(), end: buildEnd(),
      reminder_minutes: f.reminder === "" ? null : Number(f.reminder),
      reminder_email: !!f.reminder_email,
    };
    try {
      if (isEdit) await api.put(`/calendar/events/${data.id}`, payload);
      else await api.post("/calendar/events", payload);
      toast.success(isEdit ? t("calendar.toastUpdated") : t("calendar.toastAdded"));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("calendar.error")); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(t("calendar.confirmDelete"))) return;
    setBusy(true);
    try { await api.delete(`/calendar/events/${data.id}`); toast.success(t("calendar.toastDeleted")); onSaved(); }
    catch { toast.error(t("calendar.error")); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="event-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-md flex flex-col max-h-[92dvh] rounded-t-2xl sm:rounded-none overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[#1E293B] shrink-0">
          <div className="font-display font-bold text-lg">{isEdit ? t("calendar.editEvent") : t("calendar.newEvent")}</div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#E2E8F0]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="flex rounded-lg border border-[#1E293B] overflow-hidden">
            <button onClick={() => setF({ ...f, context: "firma" })} data-testid="evt-ctx-firma" className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 ${f.context === "firma" ? "bg-[#00BFFF] text-[#03070E]" : "text-[#94A3B8]"}`}><Briefcase size={15} /> {t("calendar.ctxFirma")}</button>
            <button onClick={() => setF({ ...f, context: "personal" })} data-testid="evt-ctx-personal" className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 ${f.context === "personal" ? "bg-[#A78BFA] text-[#03070E]" : "text-[#94A3B8]"}`}><UserIcon size={15} /> {t("calendar.ctxPersonal")}</button>
          </div>
          <div>
            <label className="overline block mb-2">{t("calendar.titleLabel")}</label>
            <input className="input-flat" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={t("calendar.titlePh")} data-testid="evt-title" autoFocus />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className="overline block mb-2">{t("calendar.dateLabel")}</label>
              <input type="date" className="input-flat" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="evt-date" />
            </div>
            <div>
              <label className="overline block mb-2">{t("calendar.timeLabel")}</label>
              <input type="time" className="input-flat" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="evt-time" />
            </div>
            <div>
              <label className="overline block mb-2">{t("calendar.endLabel")}</label>
              <input type="time" className="input-flat" value={f.endTime} onChange={(e) => setF({ ...f, endTime: e.target.value })} data-testid="evt-end" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">{t("calendar.noteLabel")}</label>
            <textarea rows={3} className="input-flat" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={t("calendar.notePh")} data-testid="evt-note" />
          </div>
          <div>
            <label className="overline block mb-2 flex items-center gap-1"><Bell size={13} /> {t("calendar.reminderLabel")}</label>
            <select className="input-flat" value={f.reminder} onChange={(e) => setF({ ...f, reminder: e.target.value })} data-testid="evt-reminder">
              {REMINDERS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          {f.reminder !== "" && (
            <label className="flex items-center gap-3 cursor-pointer" data-testid="evt-email-toggle">
              <input type="checkbox" checked={f.reminder_email} onChange={(e) => setF({ ...f, reminder_email: e.target.checked })} className="w-4 h-4 accent-[#00BFFF]" />
              <span className="text-sm text-[#CBD5E1]">{t("calendar.sendEmailToo")}</span>
            </label>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 p-4 border-t border-[#1E293B] shrink-0 safe-pb">
          {isEdit ? (
            <button onClick={del} disabled={busy} className="btn-outline text-[#EF4444] border-[#EF4444] hover:bg-[#2A1416] flex items-center gap-2" data-testid="evt-delete"><Trash2 size={15} /> {t("calendar.deleteBtn")}</button>
          ) : <span />}
          <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="evt-save">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} {isEdit ? t("calendar.saveBtn") : t("calendar.addBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}

function WeekGrid({ days, items, todayStr, onSlot, onItem, dotColor, dow }) {
  const dayEvents = (d) => {
    const ds = ymd(d);
    return items.filter((it) => it.start && ymd(new Date(it.start)) === ds);
  };
  const pos = (it) => {
    const s = new Date(it.start);
    const startMin = (s.getHours() - START_HOUR) * 60 + s.getMinutes();
    const e = it.end ? new Date(it.end) : null;
    const endMin = e ? (e.getHours() - START_HOUR) * 60 + e.getMinutes() : startMin + 60;
    const top = Math.max(0, (startMin / 60) * ROW_H);
    const height = Math.max(22, ((endMin - startMin) / 60) * ROW_H);
    return { top, height };
  };
  return (
    <div className="mt-4 card-flat overflow-hidden" data-testid="week-grid">
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div className="border-b border-r border-[#1E293B]" />
        {days.map((d, i) => {
          const isToday = ymd(d) === todayStr;
          return (
            <div key={i} className={`p-2 text-center border-b border-r border-[#1E293B] ${isToday ? "bg-[#111C30]" : ""}`}>
              <div className="text-[10px] uppercase text-[#94A3B8] font-bold">{dow[i]}</div>
              <div className={`text-sm font-mono-plex mt-0.5 ${isToday ? "text-[#00BFFF] font-bold" : "text-[#E2E8F0]"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: "62vh" }}>
        <div className="grid relative" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          <div className="border-r border-[#1E293B]">
            {HOURS.map((h) => (
              <div key={h} style={{ height: ROW_H }} className="text-[10px] text-[#5B6B82] text-right pr-2 -translate-y-1.5">{String(h).padStart(2, "0")}:00</div>
            ))}
          </div>
          {days.map((d, di) => (
            <div key={di} className="relative border-r border-[#1E293B]">
              {HOURS.map((h) => (
                <div key={h} style={{ height: ROW_H }} onClick={() => onSlot(d, h)}
                  className="border-b border-[#1E293B]/50 hover:bg-[#111C30]/40 cursor-pointer" data-testid={`slot-${ymd(d)}-${h}`} />
              ))}
              {dayEvents(d).map((it) => {
                const { top, height } = pos(it);
                const c = dotColor(it);
                return (
                  <button key={it.kind + it.id} onClick={(e) => { e.stopPropagation(); onItem(it); }}
                    style={{ top, height, background: c + "22", borderLeft: `3px solid ${c}`, color: c }}
                    className="absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 text-[10px] text-left overflow-hidden hover:brightness-125" data-testid={`week-item-${it.id}`}>
                    <div className="font-semibold truncate leading-tight">{it.title}</div>
                    <div className="opacity-80 truncate leading-tight">{fmtTime(it.start)}</div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
